import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from './Logo';
import { navLinks, navLabelForPath } from './navLinks';

interface MobileNavProps {
  onProfileClick: () => void;
}

/** Footer build tag — mirrors web-front/package.json `version`. */
const APP_VERSION = 'v1.0';

/** Initials from the first/last words (single word → first two letters). */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  if (!first) return '?';
  if (words.length === 1) return first.slice(0, 2).toUpperCase();
  const last = words[words.length - 1] ?? first;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/**
 * Mobile navigation chrome (Variant B): a sticky top bar (burger · page title ·
 * profile) plus a left slide-in drawer with the section list. Rendered alongside
 * the desktop {@link Header}; `styles/mobile-nav.css` shows it only ≤768px and
 * hides the desktop `.header` there, so desktop is untouched.
 */
export function MobileNav({ onProfileClick }: MobileNavProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);

  const pageTitle = navLabelForPath(location.pathname);

  // Close the drawer on any route change (tap on an item, browser back, …).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // While open: lock body scroll, close on Esc, move focus into the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const openProfile = () => {
    setOpen(false);
    onProfileClick();
  };

  return (
    <>
      <header className="mnav-topbar">
        <button
          type="button"
          className="mnav-burger"
          aria-label="Open menu"
          aria-controls="mnav-drawer"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>

        <span className="mnav-title">{pageTitle}</span>

        {!loading && user && (
          <button
            type="button"
            className="mnav-avatar"
            aria-label="Open profile"
            onClick={openProfile}
          >
            {initialsOf(user.name)}
          </button>
        )}
        {!loading && !user && (
          <button
            type="button"
            className="mnav-login"
            onClick={() => {
              setOpen(false);
              navigate('/login');
            }}
          >
            Log in
          </button>
        )}
        {loading && <span className="mnav-avatar mnav-avatar--ghost" aria-hidden="true" />}
      </header>

      <div
        className={`mnav-backdrop${open ? ' is-open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <aside
        id="mnav-drawer"
        ref={drawerRef}
        className={`mnav-drawer${open ? ' is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Main navigation"
      >
        <div className="mnav-drawer-head">
          <Logo height={28} />
          <button
            type="button"
            className="mnav-close"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          >
            <X size={22} strokeWidth={2} />
          </button>
        </div>

        <nav className="mnav-list">
          {navLinks.map((l, i) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => (isActive ? 'mnav-item is-active' : 'mnav-item')}
              onClick={() => setOpen(false)}
            >
              <span className="mnav-num">{String(i + 1).padStart(2, '0')}</span>
              <span className="mnav-text">{l.label}</span>
              <span className="mnav-arrow" aria-hidden="true">
                →
              </span>
            </NavLink>
          ))}

          {user && (
            <button type="button" className="mnav-item mnav-item--action" onClick={openProfile}>
              <span className="mnav-num">{String(navLinks.length + 1).padStart(2, '0')}</span>
              <span className="mnav-text">Profile</span>
              <span className="mnav-arrow" aria-hidden="true">
                →
              </span>
            </button>
          )}
        </nav>

        <div className="mnav-foot">
          <span className="mnav-foot-name">{user ? user.name : 'Guest'}</span>
          <span className="mnav-foot-ver">{APP_VERSION}</span>
        </div>
      </aside>
    </>
  );
}
